"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { m } from "framer-motion";
import { Palette, Megaphone, Code2, ArrowRight, Check } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { MockupImage } from "@/components/services/mockup-image";

/* ─────────────────────────────────────────────────────────── */
/* Data – Texte unverändert, Visuals aus der Mockup-Bibliothek  */
/* (dieselbe Sprache wie Landingpage und Leistungsseiten, ohne  */
/* erfundene Kennzahlen).                                       */
/* ─────────────────────────────────────────────────────────── */

type Pillar = {
  id: "design" | "marketing" | "development";
  icon: LucideIcon;
  label: string;
  title: string;
  description: string;
  bullets: string[];
  href: string;
  visual: ReactNode;
};

const pillars: Pillar[] = [
  {
    id: "design",
    icon: Palette,
    label: "Design",
    title: "Design, das Marken formt.",
    description:
      "Von Corporate Identity bis zum finalen Webdesign – wir entwickeln visuelle Systeme, die Ihre Marke unverwechselbar und konsistent über jeden Touchpoint hinweg positionieren.",
    bullets: [
      "Logo, Corporate Design & Brand Guidelines",
      "UI / UX Design mit psychologischem Fundament",
      "Webdesign, Landingpages & Print-Design",
    ],
    href: "/dienstleistungen/branding",
    visual: <MockupImage name="branding-brand-board.webp" alt="Brand-Board der Beispielbetrieb GmbH: Logo-System, Farben, Typografie und Anwendungen" sizes="(min-width: 1024px) 400px, 100vw" />,
  },
  {
    id: "marketing",
    icon: Megaphone,
    label: "Marketing",
    title: "Marketing, das Kunden bringt.",
    description:
      "SEO, Social Media und Paid Ads – datengetrieben geplant, strategisch verknüpft und ehrlich gemessen. Wir bringen die richtigen Menschen zu Ihrer Marke.",
    bullets: [
      "SEO, Local SEO & Content-Strategie",
      "Social Media Marketing & Short Form Content",
      "Google Ads, Meta Ads & LinkedIn Performance",
    ],
    href: "/dienstleistungen/seo",
    visual: <MockupImage name="seo-report.webp" alt="SEO-Monatsbericht der Beispielbetrieb GmbH: Kennzahlen, Rankings nach Ort und Maßnahmen" sizes="(min-width: 1024px) 400px, 100vw" />,
  },
  {
    id: "development",
    icon: Code2,
    label: "Development",
    title: "Development, das Visionen trägt.",
    description:
      "Von der Landingpage bis zur nativen Mobile-App. Moderne, performante und skalierbare Software – sauber entwickelt statt zusammengeklickt.",
    bullets: [
      "Websites & Web-Apps mit Next.js / React",
      "iOS- & Android-Apps, nativ oder cross-platform",
      "Individuelle Software & API-Integrationen",
    ],
    href: "/dienstleistungen/software-entwicklung",
    visual: <MockupImage name="software-kundenportal.webp" alt="Kundenportal der Beispielbetrieb GmbH mit Projekten, Tickets und Dokumenten" sizes="(min-width: 1024px) 400px, 100vw" />,
  },
];

/* ─────────────────────────────────────────────────────────── */
/* Section                                                     */
/* ─────────────────────────────────────────────────────────── */

export function AllInOne() {
  const [active, setActive] = useState<Pillar["id"]>("design");
  const pillar = pillars.find((p) => p.id === active) ?? pillars[0];
  const PillarIcon = pillar.icon;

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: "var(--bg-secondary)" }}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Header */}
        <m.div
          className="mx-auto max-w-3xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm font-semibold text-brand-500">All in one</p>
          <h2
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl"
            style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
          >
            Alles für Ihr Business &ndash; aus einer Hand.
          </h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            Design, Marketing und Development greifen bei uns nahtlos ineinander. Keine
            Schnittstellen, keine Reibungsverluste – nur ein geschlossenes System für Ihre Marke.
          </p>
        </m.div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Bereich wählen: Design, Marketing oder Development"
          className="mx-auto mt-10 flex max-w-xl items-center justify-center gap-1 rounded-full border p-1"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
        >
          {pillars.map((p, idx) => {
            const Icon = p.icon;
            const isActive = p.id === active;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                id={`tab-${p.id}`}
                aria-selected={isActive}
                aria-controls={`pillar-${p.id}`}
                aria-label={p.label}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(p.id)}
                onKeyDown={(e) => {
                  // Pfeiltasten wechseln den Reiter (WAI-ARIA Tabs-Vorgehen)
                  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                  e.preventDefault();
                  const next = pillars[(idx + (e.key === "ArrowRight" ? 1 : pillars.length - 1)) % pillars.length];
                  setActive(next.id);
                  document.getElementById(`tab-${next.id}`)?.focus();
                }}
                className={`relative flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive ? "text-white" : "hover:bg-brand-500/5"
                }`}
                style={
                  isActive
                    ? // brand-700 (#15803d), damit der weiße 14 px-Tab-Text
                      // mit 4.9 Kontrast WCAG AA Body besteht.
                      { backgroundColor: "var(--color-brand-700)" }
                    : { color: "var(--text-secondary)" }
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{p.label}</span>
              </button>
            );
          })}
        </div>

        {/* Alternating feature row */}
        <m.div
          key={pillar.id}
          role="tabpanel"
          id={`pillar-${pillar.id}`}
          aria-labelledby={`tab-${pillar.id}`}
          className="mt-12 grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          {/* Visual – Höhe kommt vom Mockup, Handy-Überhänge brauchen Luft unten */}
          <div className={`order-last min-w-0 w-full pb-4 ${active === "marketing" ? "lg:order-first" : ""}`}>
            {pillar.visual}
          </div>

          {/* Text */}
          <div className="max-w-xl">
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
              style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}
            >
              <PillarIcon className="h-3.5 w-3.5" />
              {pillar.label}
            </div>
            <h3
              className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              {pillar.title}
            </h3>
            <p className="mt-4 text-base leading-relaxed sm:text-lg" style={{ color: "var(--text-tertiary)" }}>
              {pillar.description}
            </p>

            <ul className="mt-6 space-y-3">
              {pillar.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3" style={{ color: "var(--text-secondary)" }}>
                  <span
                    className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full"
                    style={{ backgroundColor: "var(--fi-brand-bg)" }}
                  >
                    <Check className="h-3 w-3" style={{ color: "var(--fi-brand-fg)" }} />
                  </span>
                  <span className="text-sm sm:text-base">{b}</span>
                </li>
              ))}
            </ul>

            <Link
              href={pillar.href}
              className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 transition-all hover:gap-2.5"
            >
              Leistung im Detail
              <ArrowRight className="h-4 w-4 transition-transform" />
            </Link>
          </div>
        </m.div>

        {/* Bottom proof-row */}
        <div
          className="mx-auto mt-20 grid max-w-5xl grid-cols-1 gap-px overflow-hidden rounded-2xl border sm:grid-cols-3"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--border)" }}
        >
          {[
            {
              label: "Ein Ansprechpartner",
              description: "Ein Projektteam für Design, Marketing und Development – ohne Übergaben.",
            },
            {
              label: "Abgestimmter Look",
              description: "Corporate Design, Website und Kampagnen folgen derselben Designsprache.",
            },
            {
              label: "Messbare Wirkung",
              description: "Jede Maßnahme wird geplant, umgesetzt und ehrlich ausgewertet.",
            },
          ].map((cell) => (
            <div key={cell.label} className="p-6" style={{ backgroundColor: "var(--card-bg)" }}>
              <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                {cell.label}
              </p>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                {cell.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
